// src/components/TaskCard.jsx
import React, { useState } from 'react';
import {
  Card,
  Button,
  Badge,
  Dropdown,
  Modal,
  Form
} from 'react-bootstrap';
import { useTasks } from '../contexts/TasksContext';

const TaskCard = ({
  task,
  onView,
  onEdit,
  onDelete,
  onComplete,
  onAssign,
  getStatusBadgeVariant,
  getPriorityBadgeVariant
}) => {
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [actualHours, setActualHours] = useState('');
  const { getFullUserName } = useTasks(); 

  const handleComplete = () => {
    if (actualHours) {
      onComplete(task.id, parseInt(actualHours));
    } else {
      onComplete(task.id);
    }
    setShowCompleteModal(false);
    setActualHours('');
    
    // Закрываем dropdown если он открыт
    const dropdown = document.querySelector('.dropdown-menu.show');
    if (dropdown) {
      dropdown.classList.remove('show');
    }
  };

  const handleAssign = (assigneeId) => {
    if (assigneeId) {
      onAssign(task.id, parseInt(assigneeId));
      setShowAssignModal(false);
      
      // Закрываем dropdown если он открыт
      const dropdown = document.querySelector('.dropdown-menu.show');
      if (dropdown) {
        dropdown.classList.remove('show');
      }
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'open': 'Открыта',
      'assigned': 'Назначена',
      'in_progress': 'В работе',
      'completed': 'Выполнена',
      'cancelled': 'Отменена',
      'reopened': 'Переоткрыта'
    };
    return statusMap[status] || status;
  };

  const getPriorityText = (priority) => {
    const priorityMap = {
      'low': 'Низкий',
      'medium': 'Средний',
      'high': 'Высокий',
      'urgent': 'Срочный'
    };
    return priorityMap[priority] || priority;
  };

  const getCategoryText = (category) => {
    const categoryMap = {
      'technical': 'Техническая',
      'administrative': 'Административная',
      'feature_request': 'Запрос функционала',
      'bug': 'Ошибка',
      'other': 'Другое'
    };
    return categoryMap[category] || category;
  };

  const getFullName = (user) => {
    if (!user) return 'Не назначен';
    
    // Если пользователь загружается
    if (user.loading) {
      return 'Загрузка...';
    }
    
    // Если пользователь имеет ошибку (заглушка)
    if (user.error) {
      return user.name || `Пользователь #${user.id}`;
    }
    
    const firstName = user.telegram_first_name || '';
    const lastName = user.telegram_last_name || '';
    const userName = user.name || '';
    
    // Предпочтение отдаем Telegram данным
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (userName) {
      return userName;
    }
    
    return 'Пользователь';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const isOverdue = task && task.due_date && new Date(task.due_date) < new Date() && 
                 !['completed', 'cancelled'].includes(task.status);

  const canEdit = task && task.status !== 'completed';

  // Функция для фиксации dropdown меню
  const fixDropdownMenu = (isOpen, e) => {
    if (isOpen && e) {
      const dropdownMenu = e.target?.nextElementSibling;
      if (dropdownMenu && dropdownMenu.classList.contains('dropdown-menu')) {
        setTimeout(() => {
          dropdownMenu.style.zIndex = '9999';
          dropdownMenu.style.position = 'absolute';
        }, 10);
      }
    }
  };

  return (
    <>
      <Card className="h-100 task-card">
        <Card.Header className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center mb-2">
              <Badge 
                bg={getStatusBadgeVariant(task.status)} 
                className="me-2"
              >
                {getStatusText(task.status)}
              </Badge>
              <Badge 
                bg={getPriorityBadgeVariant(task.priority)}
              >
                {getPriorityText(task.priority)}
              </Badge>
              {isOverdue && (
                <Badge bg="danger" className="ms-2">
                  <i className="bi bi-clock me-1"></i>
                  Просрочена
                </Badge>
              )}
            </div>
            <h6 className="mb-0 task-title" title={task.title}>
              {task.title}
            </h6>
          </div>
          
          <Dropdown onToggle={fixDropdownMenu}>
            <Dropdown.Toggle 
              variant="outline-secondary" 
              size="sm"
              className="border-0"
            >
              <i className="bi bi-three-dots-vertical"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-fixed">
              <Dropdown.Item onClick={onView}>
                <i className="bi bi-eye me-2"></i>
                Просмотреть
              </Dropdown.Item>
              {canEdit && (
                <Dropdown.Item onClick={onEdit}>
                  <i className="bi bi-pencil me-2"></i>
                  Редактировать
                </Dropdown.Item>
              )}
              {task.status !== 'completed' && (
                <>
                  <Dropdown.Item onClick={() => setShowCompleteModal(true)}>
                    <i className="bi bi-check-lg me-2"></i>
                    Завершить
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setShowAssignModal(true)}>
                    <i className="bi bi-person-plus me-2"></i>
                    Назначить
                  </Dropdown.Item>
                </>
              )}
              <Dropdown.Divider />
              <Dropdown.Item 
                onClick={() => onDelete(task.id)}
                className="text-danger"
              >
                <i className="bi bi-trash me-2"></i>
                Удалить
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Card.Header>
        
        <Card.Body>
          {task.description && (
            <p className="task-description text-muted small">
              {task.description.length > 100 
                ? `${task.description.substring(0, 100)}...` 
                : task.description
              }
            </p>
          )}
          
          <div className="task-meta">
            <div className="mb-2">
              <small className="text-muted">Категория:</small>
              <div className="fw-semibold">{getCategoryText(task.category)}</div>
            </div>
            
            {task.estimated_hours && (
              <div className="mb-2">
                <small className="text-muted">Оценка времени:</small>
                <div className="fw-semibold">{task.estimated_hours} ч</div>
              </div>
            )}
            
            {task.actual_hours && (
              <div className="mb-2">
                <small className="text-muted">Фактическое время:</small>
                <div className="fw-semibold text-success">{task.actual_hours} ч</div>
              </div>
            )}
            
            {task.due_date && (
              <div className="mb-2">
                <small className="text-muted">Срок:</small>
                <div className={`fw-semibold ${isOverdue ? 'text-danger' : ''}`}>
                  {formatDate(task.due_date)}
                </div>
              </div>
            )}
          </div>
        </Card.Body>
        
        <Card.Footer className="bg-transparent">
          <div className="d-flex justify-content-between align-items-center">
            <div className="task-assignee">
              <small className="text-muted">Исполнитель:</small>
              <div className="fw-semibold">
                {getFullName(task.assignee)}
              </div>
            </div>
            
            <Button
              variant="outline-primary"
              size="sm"
              onClick={onView}
            >
              <i className="bi bi-eye me-1"></i>
              Подробнее
            </Button>
          </div>
        </Card.Footer>
      </Card>

      {/* Модальное окно завершения задачи */}
      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-check-circle me-2 text-success"></i>
            Завершение задачи
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Вы уверены, что хотите завершить задачу "<strong>{task.title}</strong>"?</p>
          
          <Form.Group>
            <Form.Label>Фактическое время выполнения (часы)</Form.Label>
            <Form.Control
              type="number"
              value={actualHours}
              onChange={(e) => setActualHours(e.target.value)}
              placeholder="Введите фактическое время"
              min="1"
              max="1000"
            />
            <Form.Text className="text-muted">
              Оставьте пустым, если не хотите указывать
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCompleteModal(false)}>
            Отмена
          </Button>
          <Button variant="success" onClick={handleComplete}>
            <i className="bi bi-check-lg me-2"></i>
            Завершить задачу
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно назначения задачи */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-plus me-2 text-info"></i>
            Назначение задачи
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Назначить задачу "<strong>{task.title}</strong>" пользователю:</p>
          
          <Form.Group>
            <Form.Label>Выберите пользователя</Form.Label>
            <Form.Select
              onChange={(e) => handleAssign(e.target.value)}
              required
            >
              <option value="">Выберите исполнителя</option>
              <option value="1">Иван Иванов (ID: 1)</option>
              <option value="2">Петр Петров (ID: 2)</option>
              <option value="3">Мария Сидорова (ID: 3)</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Выберите пользователя, которому хотите назначить задачу
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={() => {
            const select = document.querySelector('select');
            if (select.value) {
              handleAssign(select.value);
            }
          }}>
            <i className="bi bi-person-check me-2"></i>
            Назначить
          </Button>
        </Modal.Footer>
      </Modal>
      
    </>
  );
};

export default TaskCard;